import React, { useState } from 'react';
import { useController, Control, FieldValues, Path } from 'react-hook-form';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

import { FiEye, FiEyeOff } from 'react-icons/fi';

interface CustomInputProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  helperText?: string;

  // NEW
  type?: 'text' | 'password' | 'email' | 'number';

  inputType?: 'text' | 'alphanumeric' | 'numeric' | 'decimal' | 'alphabets' | 'utr';
  maxLength?: number;
}

const CustomInput = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  required = false,
  disabled = false,
  className = '',
  helperText,
  type = 'text',
  inputType = 'text',
  maxLength,
}: CustomInputProps<T>) => {
  const [showPassword, setShowPassword] = useState(false);

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (inputType === 'utr') {
      // Strip anything that isn't a letter/number (rejects spaces & special chars,
      // and trims leading/trailing whitespace as a side-effect) and normalize case.
      field.onChange(value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase());
      return;
    }

    if (value === '') {
      field.onChange('');
      return;
    }

    let isValid = true;
    let regex: RegExp;

    switch (inputType) {
      case 'numeric':
        regex = /^\d+$/;
        isValid = regex.test(value);
        break;
      case 'decimal':
        regex = /^\d*\.?\d*$/;
        isValid = regex.test(value);
        break;
      case 'alphabets':
        regex = /^[a-zA-Z\s]*$/;
        isValid = regex.test(value);
        break;
      case 'alphanumeric':
        regex = /^[a-zA-Z0-9\s]*$/;
        isValid = regex.test(value);
        break;
      default:
        isValid = true;
    }

    if (isValid) {
      field.onChange(value);
    }
  };

  const resolvedType = type === 'password' ? (showPassword ? 'text' : 'password') : type;

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-black dark:text-white mb-2">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          {...field}
          id={name}
          type={resolvedType}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          onChange={handleInputChange}
          className={`
            w-full px-2 rounded-[4px] py-2 pr-10  border dark:border-[#636173] transition-all duration-200 placeholder:text-[12px]
            focus:outline-none focus:ring-1 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-[#072C50]
            ${
              error
                ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                : 'border-black focus:border-gray-400 focus:ring-gray-300'
            }
          `}
        />

        {/* Password Toggle */}
        {type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2  -translate-y-1/2 text-black dark:text-white"
          >
            {showPassword ? <FaEye size={16} /> : <FaEyeSlash size={16} />}
          </button>
        )}
      </div>

      {helperText && !error && <p className="mt-1 text-xs text-gray-500">{helperText}</p>}

      {error && <p className="mt-1 text-xs text-red-600">{error.message}</p>}
    </div>
  );
};

export default CustomInput;
